import React, {
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Flex,
    FlexProps,
    Grid,
    Text,
} from '@chakra-ui/react';
import {
    BlockHeader,
    GridCard,
    ImageModel,
    useAppDispath,
} from 'shared';
import { useSearchParams } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { Tabs } from 'shared/ui/Tabs';
import { OutdoorBarArticleCardModel, OutdoorBarArticleCategoryModel } from 'entities';
import { LeadSmallSection } from 'features';
import { Helmet } from 'react-helmet-async';
// eslint-disable-next-line no-restricted-imports
import { richTextToString } from 'shared/lib/richtext-to-string';
import {
    useOutdoorBarsPageState,
    OutdoorBarsPageActions,
} from '../../store';

/**
 * Функция, которая создаёт компонент лейбла для таба
 *
 * @param {string} label Текст
 * @returns {JSX.Element} Компонент
 */
const createOutdoorBarArticlesTabsItemLabel = (
    label: string,
) => (
    <Text fontSize="1.5rem">{label.toLocaleUpperCase()}</Text>
);

/**
 * Функция, которая создаёт компонент контента в табе
 *
 * @param {OutdoorBarArticleModel[]} outdoorBarArticles Список позиций кондитерской
 * @returns {JSX.Element} Компонент
 */
const createOutdoorBarArticlesTabsContentItem = (
    outdoorBarArticles: OutdoorBarArticleCardModel[],
) => (
    <Grid
        key="all"
        templateColumns={{
            base: '1fr 1fr',
            lg: '1fr 1fr 1fr 1fr',
        }}
        gap="2rem"
    >
        {outdoorBarArticles.map(({
            uid,
            name: name2,
            image,

        }) => (
            <GridCard
                label={name2}
                to={`/outdoor-bars/${uid}`}
                src={image?.url}
                aspectRatio="2 / 3"
            />
        ))}
    </Grid>
);

/**
 * Функция, которая создаёт список компонентов для табов
 *
 * @param {OutdoorBarArticleModel[]} outdoorBarArticles Список позиций кондитерской
 * @param {OutdoorBarArticleCategoryTagModel[]} sweetShopArticleCategoryTags
 * Список тегов для текущей категории
 * @returns {JSX.Element} Параметр items в TabsProps
 */
const createOutdoorBarArticlesTabsItems = (
    outdoorBarArticles: OutdoorBarArticleCardModel[],
    outdoorBarArticleCategory: OutdoorBarArticleCategoryModel[],
) => outdoorBarArticleCategory.map(({
    uid,
    name,
}) => ({
    key: uid,
    labelComponent: createOutdoorBarArticlesTabsItemLabel(name),
    contentComponent: createOutdoorBarArticlesTabsContentItem(outdoorBarArticles),
}));

export type OutdoorBarsPageMainProps = {
    title: string,
    description?: any,
    image: ImageModel,
} & FlexProps & React.RefAttributes<HTMLDivElement>

export const OutdoorBarsPageMain: React.FC<OutdoorBarsPageMainProps> = ({
    title,
    description,
    image,
    ...props
}) => {
    const {
        outdoorBarArticleCategories,
        outdoorBarArticleCards,
        leadFormPdf,
    } = useOutdoorBarsPageState();
    const dispatch = useAppDispath();
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedTabKey, setSelectedTabKey] = useState(searchParams.get('tab') || undefined);

    useEffect(() => {
        const urlTab = searchParams.get('tab');
        if (urlTab && urlTab !== selectedTabKey) {
            setSelectedTabKey(urlTab);
        }
    }, [searchParams.get('tab')]);

    useEffect(() => {
        if (!selectedTabKey) {
            setSelectedTabKey(outdoorBarArticleCategories.at(0)?.uid);
        }
    }, [outdoorBarArticleCategories]);

    useEffect(() => {
        if (selectedTabKey) {
            dispatch(OutdoorBarsPageActions.getOutdoorBarArticleCardsByCategoryUid({
                categoryUid: selectedTabKey,
            }));
            if (selectedTabKey !== searchParams.get('tab')) {
                setSearchParams({
                    tab: selectedTabKey,
                });
            }
        }
    }, [selectedTabKey]);

    /**
     * Мемоизированные табы
     */
    const memoTabsItems = useMemo(() => (
        createOutdoorBarArticlesTabsItems(
            outdoorBarArticleCards,
            outdoorBarArticleCategories,
        )
    ), [outdoorBarArticleCards, outdoorBarArticleCategories]);

    return (
        <Flex
            {...props}
            flexDirection="column"
        >
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={richTextToString(description)} />
                <meta property="og:title" content={title} />
            </Helmet>
            <Flex>
                <BlockHeader
                    title={title}
                    imageSrc={image.url}
                    description={description}
                    width={{
                        base: '100vw',
                        sm: '100%',
                    }}
                />
            </Flex>
            <Grid
                background="#000000"
            >
                <LeadSmallSection
                    title="НЕ МОЖЕТЕ ОПРЕДЕЛИТЬСЯ?"
                    padding="2rem"
                    buttonText="Заказать звонок"
                    background="#000000"
                    maxWidth={{
                        base: '425px',
                        lg: '1280px',
                    }}
                    width="100%"
                    margin="0 auto"
                    pdfUrl={leadFormPdf?.url}
                />
            </Grid>
            <Tabs
                background="white"
                value={selectedTabKey}
                onValueChange={(v) => setSelectedTabKey(v)}
                items={memoTabsItems}
                maxWidth={{
                    base: '425px',
                    lg: '1280px',
                }}
                width="100%"
                margin="0 auto"
                padding="2rem"
            />
        </Flex>
    );
};
